"use client";

import { useApolloClient } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";

import { MentionAccountSearchDocument } from "@/graphql/codegen/graphql";
import { isAbortError } from "@/utils/helpers";
import type { PartialAccount } from "@/utils/types";

import {
  createMentionAccountSearchVariables,
  getCachedMentionSearchAccounts,
  getMentionSearchCacheKey,
  normalizeMentionSearchAccounts,
  normalizeMentionSearchQuery,
} from "./mention-search";

type MentionSearchState = {
  accounts: PartialAccount[];
  loading: boolean;
};

export const useMentionSearch = (query: string): MentionSearchState => {
  const apolloClient = useApolloClient();
  const requestIdRef = useRef(0);
  const cacheRef = useRef(new Map<string, PartialAccount[]>());
  const [state, setState] = useState<MentionSearchState>({
    accounts: [],
    loading: false,
  });

  useEffect(() => {
    const variables = createMentionAccountSearchVariables(query);
    const normalizedQuery = normalizeMentionSearchQuery(query);
    const requestId = ++requestIdRef.current;

    if (!variables || !normalizedQuery) {
      setState({
        accounts: [],
        loading: false,
      });
      return;
    }

    const cachedAccounts = getCachedMentionSearchAccounts(
      cacheRef.current,
      normalizedQuery,
    );

    setState({
      accounts: cachedAccounts,
      loading: true,
    });

    const abortController = new AbortController();

    void apolloClient
      .query({
        context: {
          fetchOptions: {
            signal: abortController.signal,
          },
        },
        fetchPolicy: "cache-first",
        query: MentionAccountSearchDocument,
        variables,
      })
      .then(({ data }) => {
        if (
          abortController.signal.aborted ||
          requestIdRef.current !== requestId
        ) {
          return;
        }

        const accounts = normalizeMentionSearchAccounts(
          data?.accounts?.nodes ?? [],
        );

        cacheRef.current.set(
          getMentionSearchCacheKey(normalizedQuery),
          accounts,
        );
        setState({
          accounts,
          loading: false,
        });
      })
      .catch((error) => {
        if (isAbortError(error) || requestIdRef.current !== requestId) {
          return;
        }

        setState((current) => ({
          ...current,
          loading: false,
        }));
        console.error(error);
      });

    return () => {
      abortController.abort();
    };
  }, [apolloClient, query]);

  return state;
};
